import SimpleDelegator from '../simple_delegator';

/**
 * Resolves/rejects every still-subscribed caller of `entry` with the real request's
 * outcome, then clears it. Should run once, only when the real underlying request
 * settles on its own.
 *
 * @param {Map} inflight the static `CoalescedRequest.inflight` map
 * @param {Object} entry the in-flight entry being settled
 * @param {String} key
 * @param {('resolve'|'reject')} status
 * @param {*} value - the value/error to settle each subscriber's promise with
 */
function settle(inflight, entry, key, status, value) {
  // Guards against deleting a newer, unrelated entry that may have since been created
  // for the same key, if this entry was already fully canceled.
  if (inflight.get(key) === entry) {
    inflight.delete(key);
  }

  entry.subscribers.forEach((subscription) => subscription[status].call(null, value));
  entry.subscribers.clear();
}

/**
 * @typedef {Object} Entry
 * @property {Map<CoalescedRequest,Subscriber>} subscribers
 * @property {FetchRequest} target
 */
/**
 * Finds or creates the in-flight entry for `key`, firing the real underlying request via
 * `target` and registering a fresh entry if none is already in flight.
 *
 * @param {Map<String,Entry>} inflight - the static `CoalescedRequest.inflight` map
 * @param {String} key - the request's method+URL key
 * @param {FetchRequest} target - the real underlying request to perform/cancel
 * @return {Entry}
 */
function entryFor(inflight, key, target) {
  const existing = inflight.get(key);
  if (existing) {
    return existing;
  }

  const entry = { subscribers: new Map(), target };
  inflight.set(key, entry);

  target.perform().then(
    (value) => settle(inflight, entry, key, 'resolve', value),
    (error) => settle(inflight, entry, key, 'reject', error)
  );

  return entry;
}

/**
 * @typedef {Object} Subscriber
 * @property {Promise} promise
 * @property {Function} resolve
 * @property {Function} reject
 */
/**
 * A Promise alongside its own resolve/reject, so a caller can settle it from outside the
 * executor.
 *
 * @return {Subscriber}
 */
function newSubscriber() {
  const subscription = {};
  subscription.promise = new Promise((resolve, reject) => {
    subscription.resolve = resolve;
    subscription.reject = reject;
  });
  return subscription;
}

/**
 * A decorator that deduplicates concurrent calls to `perform()`, across independently
 * constructed instances that target the same method+URL, not just repeated calls on a
 * single instance.
 *
 * The first `perform()` call for a given method+URL executes the underlying request. Any
 * other instance (wrapping its own, separately constructed request for the same
 * method+URL) that calls `perform()` while that request is still pending joins it as a
 * subscriber instead of firing its own request. Each subscriber gets its own Promise,
 * which settles with the shared request's outcome — but can also be independently
 * canceled: calling `cancel()` immediately rejects only that instance's own Promise,
 * leaving the real request and every other subscriber untouched. The underlying request
 * is only actually canceled once every subscriber has called `cancel()`.
 *
 * @example
 *   const requestA = new CoalescedRequest(new FetchRequest('GET', '/api/status'));
 *   const requestB = new CoalescedRequest(new FetchRequest('GET', '/api/status'));
 *   // Both resolve with the same response, but only one HTTP request fires:
 *   requestA.perform().then(handleA);
 *   requestB.perform().then(handleB);
 */
class CoalescedRequest extends SimpleDelegator {
  static inflight = new Map(); // key → { subscribers, target }

  constructor(request) {
    super(request);
    const { method, url } = request;
    this.key = `${method}:${url}`;
  }

  /**
   * @return {Promise}
   */
  perform() {
    const entry = entryFor(this.constructor.inflight, this.key, this.__target__);

    // This instance's own subscription to `entry`, created on first call so that repeat
    // perform() calls on the same instance keep returning the same Promise rather than
    // orphaning the one already handed out.
    if (!entry.subscribers.has(this)) {
      entry.subscribers.set(this, newSubscriber());
    }

    return entry.subscribers.get(this).promise;
  }

  cancel(reason) {
    const { inflight } = this.constructor;
    const entry = inflight.get(this.key);
    if (!entry) {
      return;
    }

    const subscription = entry.subscribers.get(this);
    entry.subscribers.delete(this);
    subscription?.reject(reason ?? new DOMException('The operation was aborted.', 'AbortError'));

    if (entry.subscribers.size === 0) {
      inflight.delete(this.key);
      entry.target.cancel(reason);
    }
  }
}

export default CoalescedRequest;
